import type { CreateProjectPayload, ProjectDto, UpdateProjectPayload } from '@shared/api/contracts';

export interface ProjectFormValues {
  id?: string;
  name: string;
  about: string;
  description: string;
  link: string;
  link_success: string;
  link_failed: string;
  link_callback: string;
  commission_transfer: boolean;
  commission_service: boolean;
  autoConvertToUsdt: boolean;
  crypto_currencies: number[];
  ips: string[];
  allowableDiscrepancyPercent: string;
}

export const createEmptyProjectForm = (): ProjectFormValues => ({
  name: '',
  about: '',
  description: '',
  link: '',
  link_success: '',
  link_failed: '',
  link_callback: '',
  commission_transfer: false,
  commission_service: false,
  autoConvertToUsdt: true,
  crypto_currencies: [],
  ips: [],
  allowableDiscrepancyPercent: '',
});

export const normalizeProjectFormValues = (values: ProjectFormValues): ProjectFormValues => ({
  ...values,
  name: values.name.trim(),
  about: values.about.trim(),
  description: values.description.trim(),
  link: values.link.trim(),
  link_success: values.link_success.trim(),
  link_failed: values.link_failed.trim(),
  link_callback: values.link_callback.trim(),
  allowableDiscrepancyPercent: values.allowableDiscrepancyPercent.trim(),
  ips: values.ips.map((ip) => ip.trim()).filter(Boolean),
});

export const mapProjectDtoToForm = (project: ProjectDto): ProjectFormValues => ({
  id: project.id,
  name: project.name ?? '',
  about: project.about ?? '',
  description: project.description ?? '',
  link: project.link ?? '',
  link_success: project.link_success ?? '',
  link_failed: project.link_failed ?? '',
  link_callback: project.link_callback ?? '',
  commission_transfer: project.commission_transfer ?? false,
  commission_service: project.commission_service ?? false,
  autoConvertToUsdt: project.autoConvertToUsdt ?? true,
  crypto_currencies: project.crypto_currencies ?? [],
  ips: project.ips ?? project.ipWhitelist ?? [],
  allowableDiscrepancyPercent: project.allowableDiscrepancyPercent
    ? String(Number(project.allowableDiscrepancyPercent) * 100)
    : '',
});

const mapProjectFormToBasePayload = (values: ProjectFormValues) => {
  const normalized = normalizeProjectFormValues(values);

  return {
    name: normalized.name,
    about: normalized.about,
    description: normalized.description,
    link: normalized.link,
    link_success: normalized.link_success,
    link_failed: normalized.link_failed,
    link_callback: normalized.link_callback,
    commission_transfer: normalized.commission_transfer,
    commission_service: normalized.commission_service,
    crypto_currencies: normalized.crypto_currencies,
    allowableDiscrepancyPercent: Number(normalized.allowableDiscrepancyPercent) / 100,
  };
};

export const mapProjectFormToCreatePayload = (values: ProjectFormValues): CreateProjectPayload => ({
  ...mapProjectFormToBasePayload(values),
  autoConvertToUsdt: values.autoConvertToUsdt,
});

export const mapProjectFormToUpdatePayload = (
  values: ProjectFormValues,
  original?: ProjectDto,
): UpdateProjectPayload => {
  const payload: UpdateProjectPayload = mapProjectFormToBasePayload(values);
  const originalFlag = original?.autoConvertToUsdt ?? true;

  if (values.autoConvertToUsdt !== originalFlag) {
    payload.autoConvertToUsdt = values.autoConvertToUsdt;
  }

  return payload;
};

/** @deprecated Use mapProjectFormToCreatePayload / mapProjectFormToUpdatePayload */
export const mapProjectFormToPayload = (values: ProjectFormValues): CreateProjectPayload =>
  mapProjectFormToCreatePayload(values);
